import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";

type SpeechRecognitionPhase = "idle" | "preparing" | "listening" | "stopping";
type SpeechRecognitionAvailability = "available" | "downloadable" | "downloading" | "unavailable";

interface SpeechRecognitionResultLike {
  0?: { transcript?: string };
}

interface SpeechRecognitionResultListLike {
  readonly length: number;
  [index: number]: SpeechRecognitionResultLike;
}

interface SpeechRecognitionEventLike {
  readonly results: SpeechRecognitionResultListLike;
}

interface SpeechRecognitionErrorEventLike {
  readonly error: string;
}

interface SpeechRecognitionInstance {
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  processLocally?: boolean;
  onend: (() => void) | null;
  onerror: ((event: SpeechRecognitionErrorEventLike) => void) | null;
  onresult: ((event: SpeechRecognitionEventLike) => void) | null;
  onstart: (() => void) | null;
  start: () => void;
  stop: () => void;
  abort: () => void;
}

interface SpeechRecognitionOptions {
  langs: string[];
  processLocally: true;
}

interface SpeechRecognitionConstructor {
  new (): SpeechRecognitionInstance;
  available: (options: SpeechRecognitionOptions) => Promise<SpeechRecognitionAvailability>;
  install?: (options: SpeechRecognitionOptions) => Promise<boolean>;
}

type NavigatorWithBrowserHints = Navigator & {
  userAgentData?: {
    brands?: readonly { brand: string }[];
  };
  brave?: unknown;
};

type WindowWithSpeechRecognition = Window & {
  SpeechRecognition?: SpeechRecognitionConstructor;
  webkitSpeechRecognition?: SpeechRecognitionConstructor;
};

interface UseChromeSpeechRecognitionOptions {
  enabled: boolean;
  scopeKey: string;
  onTranscript: (transcript: string) => void;
  onError: (message: string) => void;
  onLanguagePackInstalled: (language: string) => void;
}

function isGoogleChrome(): boolean {
  if (typeof navigator === "undefined") return false;

  const browserNavigator = navigator as NavigatorWithBrowserHints;
  if (browserNavigator.brave !== undefined) return false;

  const brands = browserNavigator.userAgentData?.brands;
  if (brands) {
    return brands.some(({ brand }) => brand === "Google Chrome");
  }

  const userAgent = navigator.userAgent;
  return (
    /\bChrome\/\d+/i.test(userAgent) &&
    !/(?:Edg|Edge|OPR|Opera|Vivaldi|SamsungBrowser|YaBrowser|DuckDuckGo|CriOS|FxiOS|Firefox|Electron)\//i.test(
      userAgent,
    )
  );
}

function getRecognitionConstructor(): SpeechRecognitionConstructor | null {
  if (typeof window === "undefined") return null;

  const speechWindow = window as WindowWithSpeechRecognition;
  for (const recognition of [
    speechWindow.SpeechRecognition,
    speechWindow.webkitSpeechRecognition,
  ]) {
    if (recognition && typeof recognition.available === "function") return recognition;
  }
  return null;
}

function getRecognitionErrorMessage(error: string, language: string): string | null {
  switch (error) {
    case "aborted":
    case "no-speech":
      return null;
    case "language-not-supported":
      return `Chrome could not find an on-device speech model for ${language}. No audio was sent to a remote transcription service.`;
    case "not-allowed":
    case "service-not-allowed":
      return "Allow microphone access for Operators in Chrome's site settings, then try again.";
    default:
      return "Chrome could not start on-device dictation. Check microphone access and try again.";
  }
}

function getUnavailableMessage(language: string): string {
  return `On-device speech recognition for ${language} is not available in this Chrome installation. No audio was sent to a remote transcription service.`;
}

export function useChromeSpeechRecognition({
  enabled,
  scopeKey,
  onTranscript,
  onError,
  onLanguagePackInstalled,
}: UseChromeSpeechRecognitionOptions) {
  const [isChrome, setIsChrome] = useState(false);
  const [phase, setPhase] = useState<SpeechRecognitionPhase>("idle");
  const phaseRef = useRef<SpeechRecognitionPhase>("idle");
  const recognitionRef = useRef<SpeechRecognitionInstance | null>(null);
  const operationIdRef = useRef(0);
  const onTranscriptRef = useRef(onTranscript);
  const onErrorRef = useRef(onError);
  const onLanguagePackInstalledRef = useRef(onLanguagePackInstalled);

  useLayoutEffect(() => {
    onTranscriptRef.current = onTranscript;
    onErrorRef.current = onError;
    onLanguagePackInstalledRef.current = onLanguagePackInstalled;
  }, [onError, onLanguagePackInstalled, onTranscript]);

  const updatePhase = useCallback((nextPhase: SpeechRecognitionPhase) => {
    phaseRef.current = nextPhase;
    setPhase(nextPhase);
  }, []);

  const stopRecognition = useCallback((abort: boolean) => {
    const recognition = recognitionRef.current;
    recognitionRef.current = null;
    if (!recognition) return;

    recognition.onend = null;
    recognition.onerror = null;
    recognition.onresult = null;
    recognition.onstart = null;
    try {
      if (abort) {
        recognition.abort();
      } else {
        recognition.stop();
      }
    } catch {
      // The recognition service may already have stopped.
    }
  }, []);

  const cancelCurrentOperation = useCallback(
    (resetPhase: boolean) => {
      operationIdRef.current += 1;
      stopRecognition(true);
      if (resetPhase) updatePhase("idle");
    },
    [stopRecognition, updatePhase],
  );

  useEffect(() => {
    setIsChrome(isGoogleChrome());
  }, []);

  useEffect(() => {
    cancelCurrentOperation(true);
    return () => cancelCurrentOperation(false);
  }, [cancelCurrentOperation, scopeKey]);
  useEffect(() => {
    if (!enabled) cancelCurrentOperation(true);
  }, [cancelCurrentOperation, enabled]);

  const startRecognition = useCallback(async () => {
    const operationId = operationIdRef.current + 1;
    operationIdRef.current = operationId;
    updatePhase("preparing");

    const isCurrentOperation = () => operationIdRef.current === operationId;
    const Recognition = getRecognitionConstructor();
    if (!Recognition) {
      updatePhase("idle");
      onErrorRef.current(
        "On-device dictation is not supported by this version of Chrome. Update Chrome and try again.",
      );
      return;
    }

    const language = navigator.language || "en-US";
    const options: SpeechRecognitionOptions = { langs: [language], processLocally: true };

    try {
      let availability = await Recognition.available(options);
      if (!isCurrentOperation()) return;

      if (availability !== "available") {
        if (availability === "unavailable" || typeof Recognition.install !== "function") {
          updatePhase("idle");
          onErrorRef.current(getUnavailableMessage(language));
          return;
        }

        const installed = await Recognition.install(options);
        if (!isCurrentOperation()) return;
        if (!installed) {
          updatePhase("idle");
          onErrorRef.current(getUnavailableMessage(language));
          return;
        }

        availability = await Recognition.available(options);
        if (!isCurrentOperation()) return;
        if (availability !== "available") {
          updatePhase("idle");
          onErrorRef.current(getUnavailableMessage(language));
          return;
        }

        updatePhase("idle");
        onLanguagePackInstalledRef.current(language);
        return;
      }

      const recognition = new Recognition();
      if (!("processLocally" in recognition)) {
        updatePhase("idle");
        onErrorRef.current(
          "On-device dictation is not supported by this version of Chrome. Update Chrome and try again.",
        );
        return;
      }

      recognition.processLocally = true;
      recognition.continuous = true;
      recognition.interimResults = true;
      recognition.lang = language;
      recognitionRef.current = recognition;

      recognition.onstart = () => {
        if (isCurrentOperation() && recognitionRef.current === recognition) {
          updatePhase("listening");
        }
      };
      recognition.onresult = (event) => {
        if (!isCurrentOperation() || recognitionRef.current !== recognition) return;

        let transcript = "";
        for (let index = 0; index < event.results.length; index += 1) {
          transcript += event.results[index]?.[0]?.transcript ?? "";
        }
        if (transcript.trim()) onTranscriptRef.current(transcript);
      };
      recognition.onerror = (event) => {
        if (!isCurrentOperation() || recognitionRef.current !== recognition) return;

        const message = getRecognitionErrorMessage(event.error, language);
        recognitionRef.current = null;
        updatePhase("idle");
        if (message) onErrorRef.current(message);
      };
      recognition.onend = () => {
        if (!isCurrentOperation() || recognitionRef.current !== recognition) return;
        recognitionRef.current = null;
        updatePhase("idle");
      };

      recognition.start();
    } catch {
      if (!isCurrentOperation()) return;
      stopRecognition(true);
      updatePhase("idle");
      onErrorRef.current(
        "Chrome could not prepare on-device dictation. Check microphone access and try again.",
      );
    }
  }, [stopRecognition, updatePhase]);

  const toggle = useCallback(() => {
    if (!isChrome || !enabled) return;

    if (phaseRef.current === "preparing") {
      cancelCurrentOperation(true);
      return;
    }
    if (phaseRef.current === "listening") {
      const recognition = recognitionRef.current;
      if (!recognition) {
        updatePhase("idle");
        return;
      }
      updatePhase("stopping");
      try {
        recognition.stop();
      } catch {
        cancelCurrentOperation(true);
      }
      return;
    }
    if (phaseRef.current === "stopping") return;

    void startRecognition();
  }, [cancelCurrentOperation, enabled, isChrome, startRecognition, updatePhase]);

  return { isChrome, phase, toggle };
}
